import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Box, Button, Grid, Typography } from '@mui/material';

import ErrorMessage from '../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../sharedModules/layout/LoadingWrapper';
import EditorToggle from '../../sharedModules/form/components/EditorToggle';

import { PhotoProposal } from '../model';

import axios from '../../sharedModules/utils/axios.utils';

interface PhotoProposalImageGridProps {
  allXoiPhotos: any[];
  photoProposal: PhotoProposal;
  fetchPhotoProposal: () => void;
}

const PhotoProposalImageGrid: FC<PhotoProposalImageGridProps> = (props) => {
  const { xoiJobId } = useParams<{ xoiJobId: string }>();

  const { photoProposal, fetchPhotoProposal, allXoiPhotos } = props;

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<string[]>([]);

  const handlePhotoClick = (photoId: string) => {
    setSelectedPhotoIds((prev) => (prev.includes(photoId) ? prev.filter((id) => id !== photoId) : [...prev, photoId]));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.post(`/photo_proposals/photo_proposals`, {
        photo_page_proposal: {
          xoi_job_id: xoiJobId,
          photo_ids: selectedPhotoIds,
        },
      });
      setErrorMessage('');
      fetchPhotoProposal();
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.error || 'Something went wrong generating the proposal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <ErrorMessage message={errorMessage} />
      </Grid>
      <Grid item xs={12}>
        <LoadingWrapper isLoading={loading}>
          {allXoiPhotos?.length > 0 ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                {allXoiPhotos?.map((photo) => {
                  const selectionIndex = selectedPhotoIds.indexOf(photo.id);
                  const isSelected = selectionIndex !== -1;
                  return (
                    <div
                      key={photo.id}
                      onClick={() => handlePhotoClick(photo.id)}
                      style={{ cursor: 'pointer', position: 'relative' }}
                    >
                      {isSelected && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 6,
                            left: 6,
                            width: 26,
                            height: 26,
                            borderRadius: '50%',
                            backgroundColor: '#1976d2',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 13,
                            fontWeight: 700,
                            zIndex: 1,
                            userSelect: 'none',
                          }}
                        >
                          {selectionIndex + 1}
                        </div>
                      )}
                      <img
                        src={photo.url}
                        alt={`Uploaded by ${photo.uploader}`}
                        style={{
                          width: '100%',
                          height: '250px',
                          objectFit: 'contain',
                          backgroundColor: '#111',
                          borderRadius: '4px',
                          border: isSelected ? '3px solid #1976d2' : '3px solid transparent',
                          boxSizing: 'border-box',
                        }}
                      />
                      <p style={{ fontSize: '11px', color: '#666', margin: '4px 0 0', wordBreak: 'break-all' }}>
                        {photo.id}
                      </p>
                      <p style={{ fontSize: '11px', color: '#666', margin: '2px 0 0' }}>
                        {photo.uploader} &middot; {new Date(photo.uploaded_at).toLocaleString()}
                      </p>
                      <div onClick={(e) => e.stopPropagation()}>
                        <EditorToggle
                          record={photo}
                          controller="photo_proposals/xoi_photos"
                          field="description"
                          afterUpdate={fetchPhotoProposal}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button variant="contained" onClick={handleSave} disabled={selectedPhotoIds.length === 0 || loading}>
                  {`Create Proposal with ${selectedPhotoIds.length} Photo${selectedPhotoIds.length !== 1 ? 's' : ''}`}
                </Button>
                {photoProposal?.download_url && (
                  <Button
                    variant="outlined"
                    component="a"
                    href={photoProposal.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View PDF
                  </Button>
                )}
              </Box>
            </>
          ) : (
            <Typography>No photos for this Xoi Job</Typography>
          )}
        </LoadingWrapper>
      </Grid>
    </Grid>
  );
};

export default PhotoProposalImageGrid;
